import { z } from 'zod';

/**
 * Cross-feature primitive schemas (blueprint §3.1, §6.1).
 *
 * Promotion reason: every one of these is consumed by employees, declarations,
 * and claims from the outset — they are not a feature's types moved here for
 * convenience (§5.2).
 *
 * All validation lives here so a rule exists in exactly one place. A second
 * definition of "what a valid national ID looks like" is how two parts of a
 * system quietly disagree about the same person.
 */

/** Firestore document id. Rejects path separators, which would break rules. */
export const entityIdSchema = z
  .string()
  .min(1, 'المعرّف مطلوب')
  .max(128, 'المعرّف أطول من المسموح')
  .regex(/^[A-Za-z0-9_-]+$/, 'المعرّف يقبل الحروف والأرقام والشرطات فقط');

/**
 * National ID — digits only, no fixed length.
 *
 * Yemeni identity documents do not follow one length or prefix that this
 * project can rely on, so the schema constrains what it can defend: digits, and
 * a range that rejects obvious rubbish without rejecting a real document.
 *
 * An earlier version enforced the Saudi 10-digit format. That came from the
 * design mockups' sample data and was simply wrong. A validator that is
 * confidently wrong about a national ID locks real employees out of their own
 * records, which is worse than one that is deliberately permissive and lets the
 * committee verify against the document itself.
 */
export const nationalIdSchema = z
  .string()
  .trim()
  .regex(/^[0-9]+$/, 'رقم الهوية يجب أن يتكون من أرقام فقط')
  .min(6, 'رقم الهوية قصير جدًا')
  .max(20, 'رقم الهوية أطول من المسموح');

/** Internal payroll/employee number. Free-form per the school's own scheme. */
export const employeeNumberSchema = z
  .string()
  .trim()
  .min(1, 'الرقم الوظيفي مطلوب')
  .max(32, 'الرقم الوظيفي أطول من المسموح');

/**
 * Person name.
 *
 * Deliberately not restricted to an Arabic-only character range: staff names
 * legitimately include Latin letters, and a regex that assumes otherwise
 * silently locks real people out of their own records.
 */
export const personNameSchema = z
  .string()
  .trim()
  .min(2, 'الاسم قصير جدًا')
  .max(120, 'الاسم أطول من المسموح');

const namePartSchema = z
  .string()
  .trim()
  .min(2, 'هذا الجزء من الاسم قصير جدًا')
  .max(40, 'هذا الجزء من الاسم أطول من المسموح');

/**
 * Four-part name, as the school records it.
 *
 * Kept as separate parts rather than one string because the committee matches
 * a self-reported name against an imported one by hand, and separate parts make
 * a near-match obvious — "same first three, different last" is a question worth
 * asking, whereas two full strings are either equal or not.
 *
 * `familyName` deliberately absorbs any remaining ancestors along with the
 * family name, because how many are recorded varies by person.
 */
export const fullNamePartsSchema = z.object({
  /** الاسم الأول */
  firstName: namePartSchema,
  /** اسم الأب */
  fatherName: namePartSchema,
  /** اسم الجد */
  grandfatherName: namePartSchema,
  /** الجد الثاني وبقية الجدود واللقب */
  familyName: namePartSchema,
});

export function formatFullName(parts: FullNameParts): string {
  return [parts.firstName, parts.fatherName, parts.grandfatherName, parts.familyName]
    .join(' ')
    .trim();
}

export type FullNameParts = z.infer<typeof fullNamePartsSchema>;

/**
 * Yemeni mobile: nine digits beginning 70, 71, 73, 77, or 78.
 *
 * Stored as the bare nine digits, not E.164. The school's own records are kept
 * this way, and rewriting them into a different notation on import would make
 * the system disagree with the paperwork it is supposed to mirror.
 *
 * Earlier versions of this file validated a Saudi `+9665…` number. That came
 * from the design mockups, which used Saudi sample data, and was wrong — the
 * school is in Yemen. Corrected on 2026-07-27 by the project owner.
 */
const YEMENI_MOBILE = /^7[01378][0-9]{7}$/;

/**
 * Accepts the ways a nine-digit number arrives from a spreadsheet — with a
 * country code, with a leading zero, with spaces or dashes — and reduces it to
 * the nine digits. Rejecting a correct number because of how it was typed
 * loses real data for no gain.
 */
export function normaliseYemeniMobile(input: string): string {
  const digits = input.replace(/[^0-9]/g, '');
  const withoutCountryCode = digits.replace(/^(?:00967|967)/, '');
  return withoutCountryCode.replace(/^0+/, '');
}

export const phoneSchema = z
  .string()
  .trim()
  .transform(normaliseYemeniMobile)
  .refine(
    (value) => YEMENI_MOBILE.test(value),
    'رقم الجوال يجب أن يكون 9 أرقام يبدأ بـ 70 أو 71 أو 73 أو 77 أو 78',
  );

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email('البريد الإلكتروني غير صحيح')
  .max(254);

/** Calendar date, `YYYY-MM-DD`, Gregorian. */
export const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'التاريخ يجب أن يكون بصيغة YYYY-MM-DD')
  .refine((value) => {
    const parsed = new Date(`${value}T00:00:00Z`);
    // `new Date('2025-02-31')` rolls over to March rather than failing, so the
    // round-trip is what actually rejects an impossible date.
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().startsWith(value);
  }, 'التاريخ غير صحيح');

/** Instant, ISO 8601 with timezone. Written by the server, never by the client. */
export const isoDateTimeSchema = z.string().datetime({ offset: true });

/**
 * Money in **fils** (1 Yemeni Rial = 100 fils), stored as an integer.
 *
 * Never a float: 0.1 + 0.2 !== 0.3 in binary floating point, and an insurance
 * fund that accumulates rounding drift across thousands of claims produces
 * totals nobody can reconcile against the bank.
 *
 * Earlier versions used halalas (Saudi Riyal), inherited from the design
 * mockups' sample data. The currency is the Yemeni Rial, two decimal places,
 * confirmed by the project owner.
 */
export const moneyFilsSchema = z
  .number()
  .int('المبلغ يجب أن يكون رقمًا صحيحًا بالفلس')
  .min(0, 'المبلغ لا يمكن أن يكون سالبًا')
  /*
   * 10 trillion rials. Deliberately far above any real figure, and deliberately
   * well inside JavaScript's exact-integer range (2^53) so that summing a
   * year's claims cannot silently lose precision.
   *
   * The previous 2^31 cap was carried over from the Saudi Riyal and would have
   * been a real constraint here: the rial trades at a far lower value, so a
   * fund covering 800 people would plausibly exceed it in annual totals.
   */
  .max(1_000_000_000_000_000, 'المبلغ أكبر من الحد المسموح');

export const filsPerRial = 100;

export function rialsToFils(rials: number): number {
  return Math.round(rials * filsPerRial);
}

export function formatFilsAsRials(fils: number): string {
  return (fils / filsPerRial).toFixed(2);
}

export const genderSchema = z.enum(['male', 'female']);

export type EntityId = z.infer<typeof entityIdSchema>;
export type NationalId = z.infer<typeof nationalIdSchema>;
export type IsoDate = z.infer<typeof isoDateSchema>;
export type IsoDateTime = z.infer<typeof isoDateTimeSchema>;
export type Gender = z.infer<typeof genderSchema>;
